import React, { useMemo, useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, CheckCircle2, Users, Sparkles, Globe2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLang } from "../contexts/LanguageContext";
import useLocalizedSEO from "../hooks/useLocalizedSEO";
import { CITIES, findCityBySlug } from "../lib/cities";

const SITE = "https://qrmenu-pro.com";

function buildJsonLd(city) {
  const url = `${SITE}${city.path}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: city.localKeyword,
      provider: { "@type": "Organization", name: "qrmenu-pro.com", url: SITE },
      areaServed: { "@type": "City", name: city.name },
      serviceType: "Digital QR Menu Platform",
      description: city.intro,
      url,
      offers: [
        { "@type": "Offer", name: "Starter", price: "20", priceCurrency: "EUR" },
        { "@type": "Offer", name: "Pro", price: "40", priceCurrency: "EUR" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: city.faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: city.name, item: url },
      ],
    },
  ];
}

const COPY = {
  trustline: {
    de: "15 Tage kostenlos · Keine Kreditkarte",
    en: "15-day free trial · No credit card",
    tr: "15 gün ücretsiz · Kredi kartı yok",
  },
  startCta: {
    de: "Jetzt kostenlos starten",
    en: "Start free today",
    tr: "Şimdi ücretsiz başla",
  },
  whyTitle: {
    de: "Warum digitale QR-Menüs in {city}?",
    en: "Why digital QR menus in {city}?",
    tr: "{city}'de neden dijital QR menü?",
  },
  whyBullets: {
    de: [
      "Sofort aktualisierbar bei Preisänderungen",
      "Automatische Sprachumschaltung (DE/EN/TR)",
      "Live Tisch-Service: Gäste rufen Kellner aus dem Menü",
      "GDPR-konform & in Europa gehostet",
    ],
    en: [
      "Update prices instantly across all devices",
      "Automatic language switching (DE/EN/TR)",
      "Live table service: guests call the waiter from the menu",
      "GDPR-compliant & hosted in Europe",
    ],
    tr: [
      "Fiyat değişikliklerinde anında güncelleme",
      "Otomatik dil değiştirme (DE/EN/TR)",
      "Canlı masa servisi: misafirler menüden garson çağırır",
      "KVKK/GDPR uyumlu, Avrupa'da barındırılır",
    ],
  },
  neighborhoodsTitle: {
    de: "Beliebte Stadtteile in {city}",
    en: "Popular neighbourhoods in {city}",
    tr: "{city}'de popüler bölgeler",
  },
  neighborhoodsLead: {
    de: "Restaurants, Cafés und Bars in folgenden Vierteln nutzen bereits qrmenu-pro.com:",
    en: "Restaurants, cafés and bars in these areas already use qrmenu-pro.com:",
    tr: "Şu mahallelerdeki restoran, kafe ve barlar qrmenu-pro.com kullanıyor:",
  },
  faqTitle: { de: "Häufige Fragen", en: "Frequently asked questions", tr: "Sık sorulan sorular" },
  otherCities: {
    de: "Auch in deiner Stadt verfügbar",
    en: "Also available in your city",
    tr: "Sizin şehrinizde de var",
  },
  ctaFinal: {
    de: "Bereit für dein digitales Menü in {city}?",
    en: "Ready for your digital menu in {city}?",
    tr: "{city}'de dijital menünüz için hazır mısınız?",
  },
  venues: { de: "Lokale", en: "Venues", tr: "Mekân" },
  multilang: { de: "Sprachen", en: "Languages", tr: "Dil" },
  daysFree: { de: "Tage gratis", en: "Days free", tr: "Gün ücretsiz" },
  viewPricing: { de: "Preise ansehen", en: "View pricing", tr: "Fiyatları gör" },
};

const tplt = (s, city) => s.replace("{city}", city);

export default function CityPage() {
  const loc = useLocation();
  const { lang: ctxLang, setLang } = useLang();

  // Slug = last URL segment (works for /qr-menue-berlin, /qr-menu-london, /qr-menu-istanbul)
  const slug = useMemo(() => {
    const parts = loc.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  }, [loc.pathname]);

  const city = useMemo(() => findCityBySlug(slug), [slug]);

  // Each city has ONE canonical language (its country's). Sync the global
  // LanguageContext so Navbar/Footer/CookieConsent display in the same language.
  useEffect(() => {
    if (city && ctxLang !== city.lang) {
      setLang(city.lang);
      try { localStorage.setItem("qrmp_lang", city.lang); } catch (e) {}
    }
  }, [city, ctxLang, setLang]);

  const titleByLang = useMemo(() => {
    if (!city) return {};
    const tail = {
      de: "QR Menu Pro – Digitale Speisekarte für Restaurants",
      en: "QR Menu Pro – Digital QR Menu for Restaurants",
      tr: "QR Menu Pro – Restoranlar için Dijital QR Menü",
    }[city.lang];
    return { [city.lang]: `${city.localKeyword} | ${tail}` };
  }, [city]);

  const descByLang = useMemo(() => {
    if (!city) return {};
    const desc = {
      de: `Digitale QR-Speisekarte für Restaurants, Cafés und Bars in ${city.name}. ${city.intro} Schnell einrichten, mehrsprachig (DE/EN/TR), GDPR-konform.`,
      en: `Digital QR menu for restaurants, cafés and bars in ${city.name}. ${city.intro} Fast setup, multilingual (DE/EN/TR), GDPR-compliant.`,
      tr: `${city.name} restoranları, kafeleri ve barları için dijital QR menü. ${city.intro} Hızlı kurulum, çok dilli (DE/EN/TR), KVKK/GDPR uyumlu.`,
    }[city.lang];
    return { [city.lang]: desc };
  }, [city]);

  const keywordsByLang = useMemo(() => {
    if (!city) return {};
    const kw = {
      de: `${city.localKeyword}, QR Speisekarte ${city.name}, Digitale Speisekarte ${city.name}, Restaurant Menü ${city.name}, Mehrsprachiges Menü`,
      en: `${city.localKeyword}, digital menu ${city.name}, restaurant menu ${city.name}, contactless menu ${city.name}, multilingual menu`,
      tr: `${city.localKeyword}, dijital menü ${city.name}, restoran menüsü ${city.name}, temassız menü ${city.name}, çok dilli menü`,
    }[city.lang];
    return { [city.lang]: kw };
  }, [city]);

  const jsonLdByLang = useMemo(() => {
    if (!city) return null;
    return { [city.lang]: buildJsonLd(city) };
  }, [city]);

  // Single-language pages: pass pathByLang with only the city's own language so the
  // hook emits a self-referencing canonical and a single self-hreflang (no cross-lang
  // alternates — there is no other language version of this URL).
  const pathByLang = useMemo(() => {
    if (!city) return null;
    return { [city.lang]: city.path };
  }, [city]);

  useLocalizedSEO({ titleByLang, descByLang, keywordsByLang, jsonLdByLang, pathByLang });

  if (!city) {
    return <Navigate to="/" replace />;
  }

  const cur = city.lang; // single source of truth for content
  const cityName = city.name;
  const otherCities = CITIES.filter((c) => c.id !== city.id).slice(0, 6);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-white via-purple-50/40 to-white text-zinc-900 dark:bg-gradient-to-b dark:from-zinc-950 dark:via-purple-950/30 dark:to-zinc-950 dark:text-zinc-100 relative overflow-hidden"
      data-testid="city-page"
      data-city={city.id}
      data-city-lang={city.lang}
      style={{ marginTop: "-4rem", paddingTop: "4rem" }}
    >
      <div className="absolute -top-40 right-0 w-[600px] h-[600px] rounded-full bg-purple-500/15 blur-[120px] pointer-events-none" />
      <div className="absolute top-[600px] -left-40 w-[500px] h-[500px] rounded-full bg-fuchsia-400/10 blur-[120px] pointer-events-none" />

      <section className="relative">
        <div className="container mx-auto px-6 lg:px-12 pt-16 lg:pt-20 pb-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-purple-500/30 bg-purple-500/10 text-purple-700 dark:text-purple-300 text-xs font-semibold">
              <MapPin className="h-3.5 w-3.5" />
              {cityName} · {city.country}
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="city-h1">
              {city.localKeyword}
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-zinc-600 dark:text-zinc-300 max-w-2xl" data-testid="city-intro">
              {city.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="rounded-2xl bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/30 h-12 px-6" data-testid="city-cta-primary">
                  {COPY.startCta[cur]} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to={cur === "de" ? "/pricing" : `/${cur}/pricing`}>
                <Button size="lg" variant="outline" className="rounded-2xl h-12 px-6">
                  {COPY.viewPricing[cur]}
                </Button>
              </Link>
            </div>
            <p className="mt-3 text-xs text-zinc-500">{COPY.trustline[cur]}</p>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl">
            <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/40 backdrop-blur p-4">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{city.venues}</div>
              <div className="text-xs text-zinc-500 mt-1">{COPY.venues[cur]}</div>
            </div>
            <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/40 backdrop-blur p-4">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">3</div>
              <div className="text-xs text-zinc-500 mt-1">{COPY.multilang[cur]}</div>
            </div>
            <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/40 backdrop-blur p-4">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">15</div>
              <div className="text-xs text-zinc-500 mt-1">{COPY.daysFree[cur]}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-6 lg:px-12 py-12">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{tplt(COPY.whyTitle[cur], cityName)}</h2>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 max-w-3xl">
            {COPY.whyBullets[cur].map((b, i) => (
              <li key={i} className="flex items-start gap-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-4">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-700 dark:text-zinc-300">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-6 lg:px-12 py-12">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{tplt(COPY.neighborhoodsTitle[cur], cityName)}</h2>
          <p className="mt-3 text-zinc-600 dark:text-zinc-400 max-w-2xl">{COPY.neighborhoodsLead[cur]}</p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="city-neighborhoods">
            {city.neighborhoods.map((n) => (
              <span key={n} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-700 dark:text-purple-300 text-sm font-medium">
                <Users className="h-3.5 w-3.5" />
                {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-6 lg:px-12 py-12">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{COPY.faqTitle[cur]}</h2>
          <div className="mt-6 space-y-3 max-w-3xl" data-testid="city-faq">
            {city.faq.map((item, i) => (
              <details key={i} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-4 group">
                <summary className="cursor-pointer font-semibold text-zinc-900 dark:text-zinc-100 flex items-center justify-between gap-3">
                  <span>{item.q}</span>
                  <Sparkles className="h-4 w-4 text-purple-500 shrink-0 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-6 lg:px-12 py-12">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Globe2 className="h-6 w-6 text-purple-500" />
            {COPY.otherCities[cur]}
          </h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="city-other-cities">
            {otherCities.map((c) => (
              <Link
                key={c.id}
                to={c.path}
                className="group rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-5 hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/10 transition-all"
                data-testid={`city-link-${c.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-base font-bold">{c.localKeyword}</div>
                    <div className="text-xs text-zinc-500 mt-1">{c.name} · {c.country}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-zinc-400 group-hover:text-purple-500 group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-6 lg:px-12 py-16">
          <div className="rounded-3xl bg-gradient-to-br from-purple-700 via-purple-800 to-fuchsia-900 text-white p-10 lg:p-14 text-center">
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">{tplt(COPY.ctaFinal[cur], cityName)}</h3>
            <p className="mt-3 text-white/80 max-w-xl mx-auto">{COPY.trustline[cur]}</p>
            <Link to="/register" className="inline-block mt-6">
              <Button size="lg" className="rounded-2xl bg-white text-purple-700 hover:bg-zinc-100 h-12 px-8 font-bold" data-testid="city-cta-final">
                {COPY.startCta[cur]}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
